import React, { useState, useEffect, useCallback, useRef } from 'react';
import * as Tabs from '@radix-ui/react-tabs';
import { motion } from 'framer-motion';
import styles from './styles.module.css';

export interface ModeTab {
  id: string;
  label: string;
  icon?: React.ReactNode;
  title: string;
  description: string;
  link?: {
    url: string;
    text: string;
  };
  media?: {
    type: 'video' | 'image' | 'youtube';
    src: string;
    poster?: string;
  };
}

interface ModeTabsProps {
  tabs: ModeTab[];
  sectionTitle?: string;
  sectionSubtitle?: string;
  autoRotateInterval?: number;
}

export const ModeTabs: React.FC<ModeTabsProps> = ({
  tabs,
  sectionTitle,
  sectionSubtitle,
  autoRotateInterval = 3000,
}) => {
  const [activeTab, setActiveTab] = useState(tabs[0]?.id || '');
  const [paused, setPaused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const rotateToNext = useCallback(() => {
    setActiveTab((current) => {
      const idx = tabs.findIndex((t) => t.id === current);
      return tabs[(idx + 1) % tabs.length].id;
    });
  }, [tabs]);

  const startTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (autoRotateInterval > 0) {
      timerRef.current = setInterval(rotateToNext, autoRotateInterval);
    }
  }, [autoRotateInterval, rotateToNext]);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!paused) {
      startTimer();
    }
    return stopTimer;
  }, [paused, startTimer, stopTimer]);

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    stopTimer();
    startTimer();
  };

  const handleMouseEnter = () => {
    setPaused(true);
    stopTimer();
  };

  const handleMouseLeave = () => {
    setPaused(false);
  };

  return (
    <div className={styles.container}>
      {(sectionTitle || sectionSubtitle) && (
        <div className={styles.header}>
          {sectionSubtitle && (
            <span className={styles.subtitle}>{sectionSubtitle}</span>
          )}
          {sectionTitle && <h2 className={styles.title}>{sectionTitle}</h2>}
        </div>
      )}

      <Tabs.Root
        value={activeTab}
        onValueChange={handleTabChange}
        className={styles.tabsRoot}
      >
        <Tabs.List
          className={styles.tabsList}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {tabs.map((tab) => (
            <Tabs.Trigger
              key={tab.id}
              value={tab.id}
              className={styles.tabTrigger}
              data-state={activeTab === tab.id ? 'active' : 'inactive'}
            >
              {tab.icon && <span className={styles.tabIcon}>{tab.icon}</span>}
              <span className={styles.tabLabel}>{tab.label}</span>
              {activeTab === tab.id && (
                <motion.div
                  className={styles.activeIndicator}
                  layoutId="activeIndicator"
                  transition={{
                    type: 'spring',
                    stiffness: 380,
                    damping: 30,
                  }}
                />
              )}
            </Tabs.Trigger>
          ))}
        </Tabs.List>

        <div
          className={styles.contentArea}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {tabs.map((tab) => {
            const isActive = tab.id === activeTab;
            return (
              <div
                key={tab.id}
                className={`${styles.contentWrapper} ${isActive ? styles.contentActive : styles.contentInactive}`}
              >
                <div
                  className={styles.mediaSection}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  {tab.media?.type === 'youtube' ? (
                    <>
                      <iframe
                        className={styles.mediaVideo}
                        src={isActive ? tab.media.src : ''}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        frameBorder="0"
                        title={tab.title}
                        loading="lazy"
                      />
                      {!paused && <div className={styles.iframeOverlay} />}
                    </>
                  ) : tab.media?.type === 'video' && isActive ? (
                    <video
                      className={styles.mediaVideo}
                      poster={tab.media.poster}
                      controls
                      playsInline
                      muted
                      preload="metadata"
                    >
                      <source src={`${tab.media.src}#t=0.001`} type="video/mp4" />
                    </video>
                  ) : tab.media?.type === 'image' ? (
                    <img
                      src={tab.media.src}
                      alt={tab.title}
                      className={styles.mediaImage}
                    />
                  ) : (
                    <div className={styles.mediaPlaceholder}>
                      <span className={styles.placeholderIcon}>
                        {tab.icon}
                      </span>
                    </div>
                  )}
                </div>

                <div className={styles.textSection}>
                  <h3 className={styles.contentTitle}>{tab.title}</h3>
                  <p className={styles.contentDescription}>
                    {tab.description}
                  </p>
                  {tab.link && (
                    <a
                      href={tab.link.url}
                      className={styles.contentLink}
                    >
                      {tab.link.text}
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Tabs.Root>
    </div>
  );
};

export default ModeTabs;
